import { noForwardCustomProps, SkeletonBox } from '@apeiron/temp'
import { styled } from '@mui/material'
import ApeironGradientBox from '@src/components/share/apeiron/ApeironGradientBox'
import { FRAGMENT_COUNT_LIMIT } from '@src/constants/battleDemo'
import { BattleDemoFragment } from '@src/types/battleDemo'
import { limitedNumber } from '@src/util/tools/number'
import Link from 'next/link'
import * as R from 'ramda'
import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Container = styled(ApeironGradientBox)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px;
  ${props => props.theme.breakpoints.up('res1024')} {
    min-width: 300px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    min-width: 180px;
  }
`

const Title = styled(SkeletonBox)`
  font-weight: 300;
  min-width: 80%;
  text-align: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 20px;
    line-height: 20px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    line-height: 12px;
  }
`

const ConsumeText = styled(
  Title,
  noForwardCustomProps(['warn'])
)<ConsumeTextProps>`
  display: inline;
  ${props => (props.warn ? 'color: red;' : '')}
`

const LimitText = styled(Title)`
  color: #aaaaaa;
  display: inline;
  font-weight: 400;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 14px;
    line-height: 14px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    line-height: 12px;
  }
`

const LinkContainer = styled(SkeletonBox)`
  min-width: 40%;
`

const OpenSeaLink = styled(Link)`
  color: #1f79ff;
  font-weight: 500;
  text-decoration: underline;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 16px;
    line-height: 16px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    line-height: 12px;
  }
`

const FragmentSection: FC<Props> = (props: Props) => {
  const { className, consume, fragment } = props

  const { t } = useTranslation()

  const showSkeleton = R.isNil(fragment)

  const burnRatio = R.propOr(0, 'burnRatio', fragment) as number

  const limit = R.propOr(0, 'total', fragment) as number

  const link = R.propOr('', 'openSeaUrl', fragment) as string

  const name = R.propOr('', 'name', fragment)

  return (
    <Container className={className}>
      <Title loading={showSkeleton}>
        <Trans
          components={{
            consume: <ConsumeText warn={consume > limit} />,
            limit: <LimitText />
          }}
        >
          {t('battle_demo.pass.fragment_count', {
            name,
            count: consume * burnRatio,
            limit: limitedNumber(limit, FRAGMENT_COUNT_LIMIT)
          })}
        </Trans>
      </Title>
      <LinkContainer loading={showSkeleton}>
        <OpenSeaLink href={link} target='_blank'>
          {t('battle_demo.pass.get_more_fragment', { name })}
        </OpenSeaLink>
      </LinkContainer>
    </Container>
  )
}

type ConsumeTextProps = {
  warn: boolean
}

type Props = {
  className?: string
  consume: number
  fragment: BattleDemoFragment | null
}
export default FragmentSection
